import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, ExternalLink, Copy, Wallet, Receipt, Clock, User, GraduationCap, MessageSquare } from 'lucide-react';

const fmtInr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};

// ── Colour tokens for status badges ──
const STATUS_TONE = {
  paid:            'bg-emerald-100 text-emerald-800 border-emerald-300',
  partially_paid:  'bg-amber-100 text-amber-800 border-amber-300',
  overdue:         'bg-rose-100 text-rose-800 border-rose-300',
  sent:            'bg-sky-100 text-sky-800 border-sky-300',
  draft:           'bg-slate-100 text-slate-700 border-slate-300',
  void:            'bg-slate-200 text-slate-600 border-slate-400 line-through',
  closed:          'bg-slate-100 text-slate-700 border-slate-300',
};

const StatusBadge = ({ status }) => {
  const key = (status || '').toLowerCase().replace(/\s+/g, '_');
  const tone = STATUS_TONE[key] || 'bg-slate-100 text-slate-600 border-slate-300';
  return <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${tone}`}>{status || '—'}</span>;
};

const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-[110px_1fr] gap-2 items-baseline text-[11.5px]" data-testid={testid}>
    <span className="text-slate-500 font-medium">{label}</span>
    <span className="text-slate-800 break-words">{children ?? <span className="text-slate-300">—</span>}</span>
  </div>
);

export const SlipInvoiceDetailModal = ({ open, onOpenChange, invoiceId, zohoOrgId, snapshotDiff }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !invoiceId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/books/invoice/${invoiceId}/slip-detail`);
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load invoice detail');
        setData(null);
      } finally { setLoading(false); }
    })();
  }, [open, invoiceId]);

  const copySummary = () => {
    if (!data) return;
    const i = data.invoice, t = data.totals;
    const batchLabel = i.batch?.batch_number
      ? `Batch ${i.batch.batch_number} (${i.batch.batch_name})`
      : i.batch_zoho ? `Batch ${i.batch_zoho} [zoho only]` : null;
    const line = [
      i.invoice_number, i.customer_name, i.client_type, i.salesperson_name,
      batchLabel,
      i.batch?.course_name || i.program_name,
      i.branch_name,
    ].filter(Boolean).join(' · ');
    const money = `₹${Math.round(t.invoice_total).toLocaleString('en-IN')} = Payments ₹${Math.round(t.payments).toLocaleString('en-IN')} + CN ₹${Math.round(t.credit_notes).toLocaleString('en-IN')}${t.adjustment ? ` + Adj ₹${Math.round(t.adjustment).toLocaleString('en-IN')}` : ''} · Balance ₹${Math.round(t.balance).toLocaleString('en-IN')}`;
    const slip = i.original_expected_payment_date && i.expected_payment_date
      ? `Slip ${i.slip_days > 0 ? '+' : ''}${i.slip_days}d (${i.original_expected_payment_date} → ${i.expected_payment_date})`
      : '';
    const text = `${line}\n${money}${slip ? '\n' + slip : ''}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success('Summary copied to clipboard'),
      () => toast.error('Clipboard blocked — please copy manually'),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="slip-invoice-detail-modal">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                <span className="font-mono">{data?.invoice?.invoice_number || 'Invoice detail'}</span>
                {data?.invoice?.status && <StatusBadge status={data.invoice.status} />}
                {data?.invoice?.sub_status && data.invoice.sub_status !== data.invoice.status && <StatusBadge status={data.invoice.sub_status} />}
                {data?.credit_notes?.length > 0 && (
                  <span className="inline-block px-2 py-0.5 rounded-full border border-violet-300 bg-violet-50 text-violet-700 text-[10px] font-bold">CN applied</span>
                )}
                {data?.invoice?.slip_days > 0 && (
                  <span className="inline-block px-2 py-0.5 rounded-full border border-orange-300 bg-orange-50 text-orange-700 text-[10px] font-bold">
                    Slipped +{data.invoice.slip_days}d
                  </span>
                )}
              </DialogTitle>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {data?.invoice?.customer_name}
                {data?.invoice?.email ? ` · ${data.invoice.email}` : ''}
              </p>
            </div>
            <div className="flex gap-1.5">
              <button type="button" onClick={copySummary} disabled={!data} data-testid="slip-detail-copy-summary"
                className="text-[11px] px-2.5 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
                <Copy className="w-3 h-3" /> Copy summary
              </button>
              {data?.invoice?.invoice_id && (zohoOrgId || data.invoice.invoice_url) && (
                <a href={zohoOrgId ? `https://books.zoho.com/app/${zohoOrgId}#/invoices/${data.invoice.invoice_id}` : data.invoice.invoice_url}
                  target="_blank" rel="noreferrer" data-testid="slip-detail-open-zoho"
                  className="text-[11px] px-2.5 py-1 rounded-md border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 flex items-center gap-1">
                  Zoho <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {data?.invoice?.enrolment_no && (
                <a href={`/training/students?focus=${encodeURIComponent(data.invoice.enrolment_no)}`}
                  target="_blank" rel="noreferrer" data-testid="slip-detail-open-student"
                  className="text-[11px] px-2.5 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1">
                  Student <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-violet-600" /></div>
        ) : !data ? (
          <p className="text-xs text-slate-400 py-8 text-center">No data.</p>
        ) : (
          <>
            {/* ─── Snapshot A → B diff (only when opened from Audit) ─── */}
            {snapshotDiff && (
              <section className="border border-indigo-200 rounded-lg p-3 bg-gradient-to-br from-indigo-50 to-white" data-testid="slip-detail-snapshot-diff">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                  <h3 className="text-[11px] font-bold text-indigo-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Snapshot change · {snapshotDiff.snap_from} → {snapshotDiff.snap_to}
                  </h3>
                  {snapshotDiff.bucket && (
                    <span className={`text-[9.5px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wide ${
                      snapshotDiff.bucket === 'left' ? 'bg-rose-50 border-rose-300 text-rose-700' :
                      snapshotDiff.bucket === 'entered' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' :
                      snapshotDiff.bucket === 'changed' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                      'bg-slate-50 border-slate-300 text-slate-600'
                    }`}>{snapshotDiff.bucket}{snapshotDiff.group ? ` · ${snapshotDiff.group}` : ''}</span>
                  )}
                </div>
                {snapshotDiff.reason && (
                  <p className="text-[10.5px] text-indigo-700 mb-2">
                    <b>Why:</b> {(snapshotDiff.reason || '').replace(/_/g, ' ')}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3 text-[11px]">
                  {[
                    { k: 'balance',                label: 'Balance',        money: true },
                    { k: 'expected_payment_date',  label: 'Expected date',  money: false },
                    { k: 'status',                 label: 'Status',         money: false },
                    { k: 'salesperson_name',       label: 'Salesperson',    money: false },
                    { k: 'cf_client_type',         label: 'Client type',    money: false },
                  ].filter((f) => {
                    const a = snapshotDiff.before?.[f.k]; const b = snapshotDiff.after?.[f.k];
                    return a !== undefined || b !== undefined;
                  }).map((f) => {
                    const a = snapshotDiff.before?.[f.k]; const b = snapshotDiff.after?.[f.k];
                    const changed = String(a ?? '') !== String(b ?? '');
                    return (
                      <div key={f.k} className={`rounded-md border px-2.5 py-1.5 ${changed ? 'bg-amber-50/60 border-amber-200' : 'bg-white border-slate-200'}`}>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{f.label}</p>
                        <p className="text-[11.5px] mt-0.5 tabular-nums">
                          <span className={changed ? 'text-slate-500 line-through' : 'text-slate-700 font-medium'}>
                            {f.money ? fmtInr(a) : (a || '—')}
                          </span>
                          {changed && (
                            <>
                              <span className="mx-1.5 text-slate-400">→</span>
                              <span className="text-slate-900 font-bold">
                                {f.money ? fmtInr(b) : (b || '—')}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {snapshotDiff.delta_balance != null && Math.abs(snapshotDiff.delta_balance) > 0.01 && (
                  <p className="text-[10.5px] text-indigo-700 mt-2 tabular-nums">
                    Balance moved by <b className={snapshotDiff.delta_balance > 0 ? 'text-rose-700' : 'text-emerald-700'}>
                      {snapshotDiff.delta_balance > 0 ? '+' : ''}{fmtInr(snapshotDiff.delta_balance)}
                    </b> between the two snapshots.
                  </p>
                )}
              </section>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* ─── LEFT: Money forensics ─── */}
            <div className="space-y-4">
              <section className="border border-slate-200 rounded-lg p-3.5 bg-gradient-to-br from-emerald-50/50 to-white">
                <h3 className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5 mb-2.5">
                  <Wallet className="w-3.5 h-3.5" /> Money flow
                </h3>
                <div className="space-y-1 text-[12px] tabular-nums">
                  <div className="flex justify-between text-slate-700"><span>Invoice total</span><b>{fmtInr(data.totals.invoice_total)}</b></div>
                  <div className="flex justify-between text-emerald-700"><span>− Payments received</span><b>{fmtInr(data.totals.payments)}</b></div>
                  <div className="flex justify-between text-violet-700"><span>− Credit notes applied</span><b>{fmtInr(data.totals.credit_notes)}</b></div>
                  {Math.abs(data.totals.adjustment) > 0.01 && (
                    <div className="flex justify-between text-slate-500"><span>− Adjustment</span><b>{fmtInr(data.totals.adjustment)}</b></div>
                  )}
                  <div className="flex justify-between text-slate-900 border-t border-slate-300 pt-1 mt-1 font-bold text-[13px]">
                    <span>= Balance now</span>
                    <span className={data.totals.balance > 0.01 ? 'text-amber-700' : 'text-emerald-700'}>{fmtInr(data.totals.balance)}</span>
                  </div>
                  {Math.abs(data.totals.identity_diff) > 1 && (
                    <p className="text-[9.5px] text-slate-400 pt-1">
                      Identity gap ≈ {fmtInr(data.totals.identity_diff)} — most often Zoho rounding or a pending sync.
                    </p>
                  )}
                </div>
              </section>

              <section className="border border-slate-200 rounded-lg p-3.5">
                <h3 className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <Wallet className="w-3.5 h-3.5" /> Payments ({data.payments.length})
                </h3>
                {data.payments.length === 0 ? (
                  <p className="text-[11px] text-slate-400 italic">No payments received yet against this invoice.</p>
                ) : (
                  <div className="space-y-2">
                    {data.payments.map((p, idx) => (
                      <div key={`${p.payment_id}-${idx}`} className="border-l-2 border-emerald-400 pl-2.5 py-1" data-testid={`slip-detail-payment-${p.payment_number}`}>
                        <div className="flex justify-between items-baseline">
                          <span className="text-[11.5px] font-semibold text-slate-800">#{p.payment_number} · {fmtDate(p.date)}</span>
                          <span className="text-[12px] font-bold text-emerald-700 tabular-nums">{fmtInr(p.applied_amount)}</span>
                        </div>
                        <div className="text-[10.5px] text-slate-500 mt-0.5">
                          <span className="font-medium">{p.mode || '—'}</span>
                          {p.reference_number && <span className="ml-2">· Ref {p.reference_number}</span>}
                          {p.applied_amount < p.total_amount && (
                            <span className="ml-2 text-slate-400">of total {fmtInr(p.total_amount)}</span>
                          )}
                          <span className="ml-2"><StatusBadge status={p.status} /></span>
                        </div>
                        {p.notes && <p className="text-[10px] text-slate-500 italic mt-1 line-clamp-2">{p.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="border border-slate-200 rounded-lg p-3.5">
                <h3 className="text-[11px] font-bold text-violet-800 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <Receipt className="w-3.5 h-3.5" /> Credit notes ({data.credit_notes.length})
                </h3>
                {data.credit_notes.length === 0 ? (
                  <p className="text-[11px] text-slate-400 italic">No credit notes applied to this invoice.</p>
                ) : (
                  <div className="space-y-2">
                    {data.credit_notes.map((c, idx) => (
                      <div key={`${c.creditnote_id}-${idx}`} className="border-l-2 border-violet-400 pl-2.5 py-1" data-testid={`slip-detail-cn-${c.creditnote_number}`}>
                        <div className="flex justify-between items-baseline">
                          <span className="text-[11.5px] font-semibold text-slate-800 font-mono">{c.creditnote_number}</span>
                          <span className="text-[12px] font-bold text-violet-700 tabular-nums">{fmtInr(c.applied_amount)}</span>
                        </div>
                        <div className="text-[10.5px] text-slate-500 mt-0.5">
                          Applied {fmtDate(c.applied_date)} · CN dated {fmtDate(c.cn_date)}
                          <span className="ml-2"><StatusBadge status={c.status} /></span>
                        </div>
                        {c.created_by_name && (
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            Created by <span className="font-semibold text-slate-700">{c.created_by_name}</span>
                            {c.created_time ? ` · ${fmtDate(c.created_time)}` : ''}
                          </div>
                        )}
                        {c.reason && <p className="text-[10.5px] text-slate-600 italic mt-1">"{c.reason}"</p>}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* ─── RIGHT: People, context, timeline ─── */}
            <div className="space-y-4">
              <section className="border border-slate-200 rounded-lg p-3.5 bg-gradient-to-br from-sky-50/50 to-white">
                <h3 className="text-[11px] font-bold text-sky-800 uppercase tracking-wider flex items-center gap-1.5 mb-2.5">
                  <User className="w-3.5 h-3.5" /> Identity & assignment
                </h3>
                <div className="space-y-1.5">
                  <Row label="Customer">{data.invoice.customer_name}</Row>
                  <Row label="Salesperson">{data.invoice.salesperson_name}</Row>
                  <Row label="Client type">{data.invoice.client_type}</Row>
                  <Row label="Branch">{data.invoice.branch_name}</Row>
                  <Row label="Invoice date">{fmtDate(data.invoice.date)}</Row>
                  <Row label="Due date">{fmtDate(data.invoice.due_date)}</Row>
                </div>
              </section>

              <section className="border border-slate-200 rounded-lg p-3.5 bg-gradient-to-br from-emerald-50/40 to-white">
                <h3 className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5 mb-2.5">
                  <GraduationCap className="w-3.5 h-3.5" /> Program & student
                </h3>
                <div className="space-y-1.5">
                  <Row label="Program">
                    {data.invoice.batch?.course_name || data.invoice.program_name}
                  </Row>
                  <Row label="Batch">
                    {data.invoice.batch ? (
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                          Batch {data.invoice.batch.batch_number || '—'}
                        </span>
                        {data.invoice.batch.batch_name && (
                          <span className="text-[10.5px] text-slate-600">{data.invoice.batch.batch_name}</span>
                        )}
                        {data.invoice.batch.batch_status && (
                          <span className={`text-[9.5px] px-1.5 py-0.5 rounded border font-semibold uppercase ${
                            data.invoice.batch.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' :
                            data.invoice.batch.batch_status === 'Closed' ? 'bg-slate-100 text-slate-600 border-slate-300' :
                            'bg-sky-50 text-sky-700 border-sky-300'}`}>{data.invoice.batch.batch_status}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-[10.5px] italic">No batch linked yet</span>
                    )}
                  </Row>
                  {data.invoice.batch?.trainer_name && (
                    <Row label="Trainer">{data.invoice.batch.trainer_name}</Row>
                  )}
                  {data.invoice.batch?.batch_type && (
                    <Row label="Schedule">
                      {data.invoice.batch.batch_type}
                      {data.invoice.batch.start_date && (
                        <span className="text-[10px] text-slate-500 ml-1">
                          · {fmtDate(data.invoice.batch.start_date)} → {fmtDate(data.invoice.batch.end_date)}
                        </span>
                      )}
                    </Row>
                  )}
                  {data.invoice.batch?.centre_name && (
                    <Row label="Centre">{data.invoice.batch.centre_name}</Row>
                  )}
                  <Row label="Enrolment">
                    {data.invoice.enrolment_no ? (
                      <a href={`/training/students?focus=${encodeURIComponent(data.invoice.enrolment_no)}`}
                        target="_blank" rel="noreferrer"
                        className="text-violet-700 hover:underline decoration-dotted font-medium">
                        {data.invoice.enrolment_no}
                      </a>
                    ) : null}
                  </Row>
                  <Row label="Registration">{data.invoice.registration_status}</Row>
                  <Row label="Email">{data.invoice.email}</Row>
                  {data.invoice.batch?.whatsapp_link && (
                    <Row label="Batch WA">
                      <a href={data.invoice.batch.whatsapp_link} target="_blank" rel="noreferrer"
                        className="text-emerald-700 hover:underline decoration-dotted text-[11px] font-medium inline-flex items-center gap-1"
                        data-testid="slip-detail-batch-whatsapp">
                        Open group <ExternalLink className="w-3 h-3" />
                      </a>
                    </Row>
                  )}
                </div>
              </section>

              <section className="border border-slate-200 rounded-lg p-3.5 bg-gradient-to-br from-amber-50/40 to-white">
                <h3 className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5 mb-2.5">
                  <Clock className="w-3.5 h-3.5" /> Slip metric
                </h3>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-white border border-slate-200 py-2">
                    <p className="text-[9px] uppercase text-slate-500 font-semibold tracking-wide">Original</p>
                    <p className="text-[11.5px] font-bold text-slate-800 mt-0.5">{fmtDate(data.invoice.original_expected_payment_date)}</p>
                  </div>
                  <div className="rounded-md bg-white border border-slate-200 py-2">
                    <p className="text-[9px] uppercase text-slate-500 font-semibold tracking-wide">Current</p>
                    <p className="text-[11.5px] font-bold text-slate-800 mt-0.5">{fmtDate(data.invoice.expected_payment_date)}</p>
                  </div>
                  <div className={`rounded-md py-2 border ${data.invoice.slip_days > 0 ? 'bg-orange-50 border-orange-300' : data.invoice.slip_days < 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-300'}`}>
                    <p className="text-[9px] uppercase text-slate-500 font-semibold tracking-wide">Δ days</p>
                    <p className={`text-[13px] font-extrabold mt-0.5 ${data.invoice.slip_days > 0 ? 'text-orange-700' : data.invoice.slip_days < 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                      {data.invoice.slip_days === null ? '—' : (data.invoice.slip_days > 0 ? '+' : '') + data.invoice.slip_days + 'd'}
                    </p>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">Slip = Current − Original. Positive means the salesperson pushed the promise later.</p>
              </section>

              <section className="border border-slate-200 rounded-lg p-3.5">
                <h3 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <MessageSquare className="w-3.5 h-3.5" /> Expected-date history ({data.events.length})
                </h3>
                {data.events.length === 0 ? (
                  <p className="text-[11px] text-slate-400 italic">No expected-date change events captured.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {data.events.map((e, i) => (
                      <div key={i} className="border-l-2 border-slate-300 pl-2.5 py-1" data-testid={`slip-detail-event-${i}`}>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-[10.5px] text-slate-500">{e.changed_at}</span>
                          <span className="text-[11px] font-semibold text-slate-800">{e.actor}</span>
                        </div>
                        {e.new_expected_date && (
                          <p className="text-[11px] text-slate-700 mt-0.5">
                            Expected date → <b>{fmtDate(e.new_expected_date)}</b>
                          </p>
                        )}
                        {e.reason && <p className="text-[10.5px] text-slate-600 italic mt-1 whitespace-pre-wrap">"{e.reason}"</p>}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SlipInvoiceDetailModal;
